import {
  CountryCode,
  PlanStatus,
  SpacePlanPaymentPriceInfo,
  SpaceSimpleDto,
} from '@zep/types';

import { Currency } from './GlobalTypes';

export const BankCodes = {
  '39': '경남은행',
  '34': '광주은행',
  '12': '단위농협(지역농축협)',
  '32': '부산은행',
  '45': '새마을금고',
  '64': '산림조합',
  '88': '신한은행',
  '48': '신협',
  '27': '씨티은행',
  '20': '우리은행',
  '71': '우체국예금보험',
  '50': '저축은행중앙회',
  '37': '전북은행',
  '35': '제주은행',
  '90': '카카오뱅크',
  '89': '케이뱅크',
  '92': '토스뱅크',
  '81': '하나은행',
  '54': '홍콩상하이은행',
  '03': 'IBK기업은행',
  '06': 'KB국민은행',
  '31': 'DGB대구은행',
  '02': 'KDB산업은행',
  '11': 'NH농협은행',
  '23': 'SC제일은행',
  '07': 'Sh수협은행',
} as const;

export enum PlanTemplateEnum {
  일시납 = 'PRODUCT_ONETIME',
  커스텀_정기 = 'CUSTOM_SUBSCRIPTION',
  자동_정기 = 'PRODUCT_SUBSCRIPTION',
}

export enum OperationType {
  일시납 = 'ONETIME_TEMPLATE',
  커스텀_일시납 = 'ONETIME_CUSTOM',
  정기 = 'SUBSCRIPTION_TEMPLATE',
  커스텀_정기 = 'SUBSCRIPTION_CUSTOM',
}

export enum PaymentStatus {
  SUCCESS = 'SUCCESS',
  REFUNDED = 'REFUNDED',
  FAILED = 'FAILED',
  WAITING_FOR_DEPOSIT = 'WAITING_FOR_DEPOSIT',
  CANCELED = 'CANCELED',
}

export interface SpacePlanListResponse {
  status: number;
  statusKey: string;
  data: SpacePlanDto[];
}

export interface SpacePlanDto
  extends Pick<PlanPrice, 'salePrice' | 'originalPrice' | 'saleType'> {
  spaceHashId: string;
  planType: PlanType;
  maxCcu: number;
  status: PlanStatus;
  currency: string;
  startDate: string;
  options: PlanOptions;
  endDate: string;
  paymentFailCount: number;
  operationType: OperationType;
  verificationCode: string;
  spacePlanHashId: string;
  additionalInformation: null | string;
  name: string;
  paymentExpectedDate: null | string;
  onetimePeriod?: string; // 현재 플랜이 일시납인 경우만 값이 존재
}

export interface VirtualAccountDto {
  accountNumber: string;
  bankCode: string;
  customerName: string;
  dueDate: string;
  finalPrice: number;
  currency: string;
}

export type VirtualAccountInfo = VirtualAccountDto | null;

export interface ReadySpacePlanWithVirtualAccountDto {
  readySpacePlan: SpacePlanDto;
  virtualAccountInfo: VirtualAccountInfo;
}

export interface PlanOptions {
  customUrl: boolean;
  customLoading: boolean;
  googleAnalytics: boolean;
  dataDashboard: boolean;
  additionalInformation: boolean;
  adPopupBlock: boolean;
  premiumApp: boolean;
  premiumFunction: boolean;
  premiumPlanServer: boolean;
  privateServer: boolean;
  ssoEnabled: boolean;
  topPriorityCs: boolean;
  enableRecording: boolean; // 녹화 기능 활성화 여부 (Admin Add-on)
}

export interface SpacePlans {
  countryCode: CountryCode;
  spacePlans: SpacePlanWithPayment[];
}

export interface SpacePlanWithPayment {
  spacePlan: SpacePlan;
  payment: SpacePlanPaymentDto;
}

export type SpacePlanPaymentType =
  | 'NONE'
  | 'ETC'
  | 'CARD'
  | 'MANUAL_TRANSFER'
  | 'VIRTUAL_ACCOUNT'
  | 'TRANSFER';

export interface SpacePlanPaymentDto {
  hashId: string;
  status: PaymentStatus;
  type: SpacePlanPaymentType;
  approvedAt: string;
  orderId: string;
  orderName: string;
  totalPrice: number;
  unusedPrice: number;
  previousPlan: string;
  cardName: string;
  cardNumber: string;
  buyerName: string;
  data: string;
  receiptUrl: string;
}

export type SubscribePlan = 'free' | 'basic' | 'pro' | 'enterprise';
export type PlanType = Uppercase<SubscribePlan>;

export type PaymentCard = {
  cardNumber: string;
  cardExpirationYear: string;
  cardExpirationMonth: string;
  customerIdentityNumber: string;
  cardName: string;
  customerEmail?: string;
  expectedResultForTest?: 'Fail';
};

export type SpacePlan = SpacePlanDto | null;

export type SubscriptionPlan = {
  spaceHashId: string;
  spacePlanTemplateId: number;
  countryCode: CountryCode;
  expectedResultForTest?: 'Fail';
  zipCode?: number;
  subscriptionRequestHashId?: string;
};

export interface PlanProduct extends PlanPrice {
  id: number;
  maxCcu: number;
  name: string;
  status: '0' | '1';
  createdAt: string;
  updatedAt: string;
  description: string;
  subscriptionPeriod?: number;
  onetimePeriod?: string;
  hashId: string;
  option: PlanOptions;
  type: 'PRODUCT_SUBSCRIPTION' | 'CUSTOM_SUBSCRIPTION' | 'PRODUCT_ONETIME';
  priceInfo?: SpacePlanPaymentPriceInfo | null;
}

export type PlansProduct = {
  [Plan in Uppercase<SubscribePlan>]: PlanProduct[];
};

export type SellingSpaceTemplateData = {
  planProducts: PlansProduct;
  showEnterpriseSample: boolean;
  customSubscriptionRequestPriceInfo?: SpacePlanPaymentPriceInfo | null;
};

export type PlanProducts = PlansProduct;

export type SpacePlanProducts = PlansProduct;

export interface SpacePlanPriceDto
  extends Omit<PlanPrice, 'currency' | 'saleDiscountRate'> {
  // 변경 예정 플랜
  spacePlanTemplateId: number;
  // 1달 - 사용남은 금액(3일 사용했으면, 남은 27일에 대한 금액)
  unusedPrice: number;
  unusedDayCount: number;
  finalPrice: number;
  isUpgrade: boolean;
  asisPlanId: number;
}

// 스페이스 결제에 대한 정보
export interface SubscriptionPayment {
  orderId: string;
  orderName: string;
  approvedAt: Date;
  data: string;
}

export type PreviousPlan = {
  planType: PlanType;
  maxCcu: number;
  startDate: string;
  endDate: string;
  unusedPrice: number;
  unusedDayCount: number;
  salePrice: number;
};

export interface InvoiceDto extends PlanPrice {
  spaceHashId: string;
  spaceName: string;
  spaceUrl: string;
  spaceOwnerEmail: string;
  approvedAt: string;
  cardName: string;
  cardNumber?: string;
  orderId: string;
  orderName: string;
  startDate: string;
  endDate: string;
  saleType: string;
  unusedPrice: number;
  finalPrice: number;
  vatAmount: number;
  vatRate: number;
  maxCcu: number;
  planType: PlanType;
  currency: Currency;
  countryCode: CountryCode;
  zipCode: string | null;
  salePrice: number;
  previousPlan?: PreviousPlan | null;
  paymentStatus: PaymentStatus;
  refundedAt: string | null;
  paymentType: SpacePlanPaymentType;
  operationType: OperationType;
  totalDayCount: number;
  onetimePeriod: 'M' | 'D' | null;
}

interface PlanPrice {
  saleType: string | null;
  salePrice: number;
  // 할인 차감 금액
  saleDiscountPrice: number;
  saleDiscountRate: number;
  originalPrice: number;
  currency: Currency;
}

export interface SpaceWithPlanDetail {
  space: SpaceSimpleDto;
  spacePlan?: SpacePlan;
  subscription: SpacePlanSubscriptionDto | null;
  billingInfo: SpaceBillingInfo | null;
  nextPaymentPriceInfo: SpacePlanPaymentPriceInfo | null;
  nextSpacePlanTemplate: SpacePlanTemplateDto;
  readySpacePlans: ReadySpacePlanWithVirtualAccountDto[];
}

export type SpacePlanPaymentInfo = Omit<
  SpaceWithPlanDetail,
  'nextSpacePlanTemplate'
>;

export type PlanDetail = {
  space: SpaceSimpleDto;
  spacePlan: SpacePlanDto;
  paymentInfo: PaymentInfo;
  subscriptionInfo?: {
    subscription: SpacePlanSubscriptionDto;
    billingInfo: SpaceBillingInfo;
    // API 계약상 optional(GetSpacePlanDetailResponse). 예약된 변경 플랜이
    // 없으면 응답에서 빠지므로 접근 시 반드시 가드해야 한다.
    nextSpacePlanTemplate?: SpacePlanTemplateDto;
  };
};

export interface PaymentInfo {
  orderId: string;
  virtualAccountInfo?: VirtualAccountInfo;
}

export interface SpacePlanTemplateDto {
  id: number;
  type: string;
  subscriptionPeriod: number;
  onetimePeriod: string;
  planType: PlanType;
  name: string;
  maxCcu: number;
  salePrice: number;
  originalPrice: number;
  saleType: string;
  saleDiscountPrice: number;
  saleDiscountRate: number;
  currency: string;
  option: PlanOptions;
  status: string;
  description: string;
  updatedAt: string;
  createdAt: string;
  hashId: string;
  priceInfo?: SpacePlanPaymentPriceInfo | null;
}

export interface SpaceBillingInfo {
  billingKey: string;
  cardName: string;
  cardNumber: string;
  approvedAt: string;
  data: string;
  zipCode: string;
}

export interface SpacePlanSubscriptionDto {
  subscriptionHashId: string;
  spaceHashId: string;
  planName: string;
  spacePlanTemplateId: number;
  subscriptionPeriod?: number;
  onetimePeriod?: string;
  planType: string;
  maxCcu: number;
  salePrice: number;
  originalPrice: number;
  saleType: string;
  currency: string;
  options: PlanOptions;
  startDate: string;
  endDate: string;
  nextCreationDate: string;
  leftCount: number;
  nextSpacePlanTemplateId: number;
  requestedAction: UserRequest;
  operationType: OperationType;
  verificationCode: string;
}

export type UserRequest = 'NONE' | 'UNSUBSCRIBE' | 'DOWNGRADE';

export interface SpaceWithPlanDto {
  spacePlanHashId: string;
  spaceHashId: string;
  spaceName: string;
  role: number;
  thumbnailUrl: string;
  spaceUrl: string;
  entryMapHashId: string;
  customDomain: string;
  planType: PlanType;
  planStatus: PlanStatus;
  paymentFailCount: number;
  salePrice: number;
  originalPrice: number;
  saleType: string;
  currency: string;
  maxCcu: number;
  operationType: PlanTemplateEnum;
  countryCode: CountryCode;
  options: PlanOptions;
  paymentExpectedDate: string;
  startDate: string;
  endDate: string;
  isWaitingForPayment: boolean;
}

export interface SpaceWithPlanListResponse {
  status: number;
  statusKey: string;
  data: MySpaceWithPlans;
}

export interface MySpaceWithPlans {
  activeSpaces: SpaceWithPlanDto[];
  expiredSpaces: SpaceWithPlanDto[];
  readySpaces: SpaceWithPlanDto[];
}
